// eslint-disable-next-line import/no-cycle
import axios from 'axios';
import { profile as schemaProfile } from './schemas';
import apiClient from './apiClient';
import ProfileDataStore from '../../datastores/ProfileDataStore';
import constants from '../../../core/constants';
import defaultPhoto from '@/core/constants/photo.json';

class ProfileDataStoreGraphQL implements ProfileDataStore {
  client: any = apiClient();

  async getEmailAndPhone() {
    try {
      const response = await this.client.query({
        query: schemaProfile.getEmailAndPhone
      });

      const { email, phone } = response.data?.student || {};
      return {
        email,
        phone
      };
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getProfile() {
    const storedData = localStorage.getItem('profileData');

    if (storedData) {
      return JSON.parse(storedData);
    }

    try {
      const response = await this.client.query({
        query: schemaProfile.getProfile
      });

      if (!response.data?.student) {
        return null;
      }
      const { academic, profile } = response.data?.student;

      const data = {
        profile: { ...profile },
        academic: {
          ...academic,
          isCGT: academic?.career === constants.CAREER.CGT
        },
        zoomAccount: response.data?.zoomAccount,
        pendingDocument: response.data?.pendingDocument
      };
      if (data) {
        localStorage.setItem('profileData', JSON.stringify(data));
      }
      return data;
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getStudentType(period: string) {
    try {
      const response = await this.client.query({
        query: schemaProfile.getStudentType,
        variables: {
          period
        }
      });

      const { studentType } = response.data?.student ?? {};
      return studentType;
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getProfilePhoto() {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfilePhoto
      });
      const { url } = response.data?.profile?.photo2 || {};
      let { photo } = defaultPhoto;
      await axios
        .get(url)
        .then(() => {
          photo = url;
        })
        .catch(() => {});
      return photo;
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getModalityOfStudy() {
    try {
      const response = await this.client.query({
        query: schemaProfile.getModalityOfStudy,
        fetchPolicy: 'cache-first'
      });
      const { modalityOfStudy } = response.data?.procedures || {};
      return modalityOfStudy;
    } catch (error) {
      throw new Error('error modality of study');
    }
  }

  async getProfileTurn(period: string) {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfileTurn,
        variables: { period },
        fetchPolicy: 'cache-first'
      });
      const { modalityOfStudy } = response.data?.procedures || {};
      const { profile, enrolmentAppointmentV2: enrolmentAppointment } =
        response.data?.student || {};
      return { profile, modalityOfStudy, enrolmentAppointment };
    } catch (error) {
      throw new Error('error turn');
    }
  }

  async getProfileTurnByPeriod(period: string) {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfileTurnByPeriod,
        variables: { period }
      });
      return response?.data?.student?.enrolmentAppointmentByPeriod;
    } catch (error) {
      throw new Error('error turn');
    }
  }

  async sendValidationCode(email: string, phone: string) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProfile.sendValidationCode,
        variables: { email, phone }
      });

      return response.data?.sendValidationCode;
    } catch (error) {
      throw new Error('Error send validation code');
    }
  }

  async validateCodeByType(validationCode: number, field: string) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProfile.validateCode,
        variables: { validationCode, field }
      });

      return response.data?.validateCode;
    } catch (error) {
      throw new Error('Error send validation code');
    }
  }

  async updateDataProfile(
    newValue: string,
    oldValue: string,
    type: string,
    validationCode: string
  ) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProfile.updateDataProfile,
        variables: { newValue, oldValue, type, validationCode }
      });

      return response.data?.updateDataProfile;
    } catch (error) {
      throw new Error('Error update data profile');
    }
  }

  async updateProfile(updateRequests: any) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProfile.updateProfile,
        variables: { updateRequests }
      });
      const { data } = response;

      return data;
    } catch (error) {
      throw new Error('Error "registerWithdrawalCourses"');
    }
  }

  async getPeriods() {
    try {
      const response = await this.client.query({
        query: schemaProfile.getPeriods
      });
      return response.data?.academicInformationV2?.periods;
    } catch (error) {
      throw new Error('Error get periods');
    }
  }

  async getProfileData() {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfileData
      });
      const data = response.data?.profileData || {};
      return data;
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getProfileMastersData(type: string) {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfileMastersData,
        variables: { type }
      });

      const { profileMastersData } = response.data ?? {};
      return profileMastersData;
    } catch (error) {
      throw new Error('error profile');
    }
  }

  async getProfileAcl(
    career: string,
    plan: string,
    prog: string,
    period: string
  ) {
    try {
      const response = await this.client.query({
        query: schemaProfile.getProfileAcl,
        variables: { params: { career, plan, prog, period } }
      });
      const { securityPortal } = response.data ?? {};
      return securityPortal;
    } catch (error: any) {
      throw new Error(`error profile ${error.message}`);
    }
  }
}

export default ProfileDataStoreGraphQL;
